/**
 * Pixelgipfel cart engine:
 * - AJAX add-to-cart from the PDP form -> opens the "Im Warenkorb" modal
 * - Qty steppers + remove on the cart page (Section Rendering API re-render)
 * - Cross-sell "+ Dazu" buttons (cart page + modal) with toast feedback
 * - Dispatches pg:cart-updated so the header pill stays in sync
 */
(function () {
  'use strict';

  var toastTimer = null;
  var busyRequests = 0;
  var lineChanges = Object.create(null);
  var mutationQueue = Promise.resolve();
  var cartReadVersion = 0;
  var appliedCartVersion = 0;
  var modalDialogToken = null;
  var cartNeedsRefresh = false;
  var cartRestoresPending = 0;
  var REQUEST_TIMEOUT_MS = 15000;

  function localRoute(path) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    root = typeof root === 'string' && root ? root : '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + String(path || '').replace(/^\/+/, '');
  }

  function timedFetch(url, options) {
    var requestOptions = options || {};
    if (typeof AbortController === 'undefined' || requestOptions.signal) return fetch(url, requestOptions);
    var controller = new AbortController();
    requestOptions.signal = controller.signal;
    var timer = setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS);
    return fetch(url, requestOptions).finally(function () { clearTimeout(timer); });
  }

  function requestJson(path, options) {
    var requestOptions = options || {};
    requestOptions.headers = Object.assign({ Accept: 'application/json' }, requestOptions.headers || {});
    return timedFetch(localRoute(path), requestOptions).then(function (response) {
      return response.text().then(function (text) {
        var data;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (_) {
          var malformed = new Error(str('invalidResponse', 'Shopify hat eine ungültige Antwort gesendet.'));
          malformed.code = 'INVALID_JSON';
          malformed.status = response.status;
          throw malformed;
        }
        if (!response.ok) {
          var rejected = new Error((data && (data.description || data.message)) || str('requestFailed', 'Shopify konnte die Anfrage nicht ausführen.'));
          rejected.code = 'HTTP_ERROR';
          rejected.status = response.status;
          rejected.data = data;
          throw rejected;
        }
        if (!data || typeof data !== 'object') {
          var invalid = new Error(str('invalidResponse', 'Shopify hat eine ungültige Antwort gesendet.'));
          invalid.code = 'INVALID_PAYLOAD';
          invalid.status = response.status;
          throw invalid;
        }
        return data;
      });
    });
  }

  function validCart(cart) {
    return cart && Array.isArray(cart.items) && typeof cart.item_count === 'number' && typeof cart.total_price === 'number';
  }

  function str(key, fallback) {
    return (window.pgStrings && window.pgStrings[key]) || fallback;
  }

  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, window.moneyFormat || "CHF {{amount_with_apostrophe_separator}}");
      } catch (_) {}
    }
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    return 'CHF ' + parts.join('.');
  }

  function dispatchCart(cart, version) {
    document.dispatchEvent(new CustomEvent('pg:cart-updated', {
      detail: { cart: cart, version: version }
    }));
  }

  function getCart() {
    var version = ++cartReadVersion;
    return requestJson('cart.js')
      .then(function (cart) {
        if (!validCart(cart)) {
          var invalid = new Error(str('invalidCart', 'Der Warenkorbstatus ist unvollständig.'));
          invalid.code = 'INVALID_CART';
          throw invalid;
        }
        if (version < appliedCartVersion) return { cart: cart, applied: false };
        appliedCartVersion = version;
        dispatchCart(cart, version);
        syncModalState(cart);
        return { cart: cart, applied: true };
      });
  }

  /* ---------- cart page section re-render ---------- */

  function cartRoot() {
    return document.querySelector('[data-pg-cart-section]');
  }

  function setBusy(state) {
    var root = cartRoot();
    if (root) root.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  function beginBusy() {
    busyRequests += 1;
    setBusy(true);
  }

  function endBusy() {
    busyRequests = Math.max(0, busyRequests - 1);
    if (busyRequests === 0) setBusy(false);
  }

  function enqueueMutation(task) {
    beginBusy();
    var run = mutationQueue.catch(function () {}).then(task);
    mutationQueue = run.catch(function () {});
    return run.finally(endBusy);
  }

  // quantity and line changes crossfade where the browser supports typed view transitions (03.10.2026)
  function swapCart(root, fresh) {
    var typed = typeof document.startViewTransition === 'function' && typeof window.ViewTransition === 'function' &&
      'types' in window.ViewTransition.prototype;
    if (!typed || document.hidden) { root.replaceWith(fresh); return; }
    try {
      document.startViewTransition({ update: function () { root.replaceWith(fresh); }, types: ['pg-cart'] });
    } catch (error) { if (root.isConnected) root.replaceWith(fresh); }
  }

  function renderCartSectionHtml(html) {
    var root = cartRoot();
    if (!root) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-pg-cart-section]');
    if (!fresh) throw new Error('Invalid cart section response');
    if (fresh) {
      swapCart(root, fresh);
      setBusy(busyRequests > 0);
      Object.keys(lineChanges).forEach(function (lineKey) { setLineBusy(lineKey, true); });
    }
  }

  function cartSectionId() {
    var root = cartRoot();
    return root && root.getAttribute('data-section-id');
  }

  function getCartSection() {
    var id = cartSectionId();
    if (!id) return Promise.resolve();
    return timedFetch(localRoute('cart?section_id=' + encodeURIComponent(id)), {
      headers: { Accept: 'text/html' }
    }).then(function (response) {
      if (!response.ok) throw new Error(str('cartRefreshError', 'Der Warenkorb konnte nicht neu geladen werden.'));
      return response.text();
    }).then(renderCartSectionHtml);
  }

  function reconcileCart() {
    var cartResult;
    var cartError;
    var sectionError;
    return Promise.all([
      getCart().then(function (result) { cartResult = result; }).catch(function (error) { cartError = error; }),
      getCartSection().catch(function (error) { sectionError = error; })
    ]).then(function () {
      setCartRefreshRequired(!!(cartError || sectionError));
      if (cartError) throw cartError;
      return { cart: cartResult.cart, sectionError: sectionError };
    });
  }

  function performMutation(post) {
    return enqueueMutation(function () {
      var response;
      var postError;
      return post()
        .then(function (result) { response = result; }, function (error) { postError = error; })
        .then(function () {
          return reconcileCart().then(function (reconciled) {
            if (postError) {
              postError.reconciledCart = reconciled.cart;
              throw postError;
            }
            return { response: response, cart: reconciled.cart, sectionError: reconciled.sectionError };
          }, function (reconcileError) {
            if (postError) {
              postError.reconcileError = reconcileError;
              throw postError;
            }
            return { response: response, cart: null, reconcileError: reconcileError };
          });
        });
    });
  }

  /* ---------- mutations ---------- */

  function changeLine(lineKey, quantity) {
    return performMutation(function () {
      if (cartNeedsRefresh) return Promise.reject(new Error('Cart display requires refresh'));
      return requestJson('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
    }).then(function (outcome) {
      if (outcome.sectionError || outcome.reconcileError) {
        throw outcome.sectionError || outcome.reconcileError;
      }
      return outcome.cart;
    });
  }

  function setCartRefreshRequired(required) {
    if (!cartRoot()) return;
    cartNeedsRefresh = required;
    document.querySelectorAll('[data-pg-cart-line]').forEach(function (line) {
      var key = line.getAttribute('data-pg-cart-line');
      setLineBusy(key, !!lineChanges[key]);
    });
    var status = document.querySelector('[data-pg-cart-status]');
    if (status) status.textContent = required ? status.getAttribute('data-refresh-error') : '';
    var reload = document.querySelector('[data-pg-cart-reload]');
    if (reload) reload.hidden = !required;
  }

  function setLineBusy(lineKey, state) {
    document.querySelectorAll('[data-pg-cart-line]').forEach(function (line) {
      if (line.getAttribute('data-pg-cart-line') !== lineKey) return;
      line.setAttribute('aria-busy', state ? 'true' : 'false');
      line.querySelectorAll('[data-pg-qty-change], [data-pg-remove]').forEach(function (control) {
        control.disabled = state || cartNeedsRefresh || cartRestoresPending > 0;
      });
    });
  }

  function queueLineChange(lineKey, quantity) {
    if (!lineKey || cartNeedsRefresh || cartRestoresPending > 0) return Promise.resolve();
    var state = lineChanges[lineKey] || { desired: quantity, sent: null, promise: null };
    state.desired = Math.max(0, quantity);
    lineChanges[lineKey] = state;
    if (state.promise) return state.promise;
    setLineBusy(lineKey, true);

    function sendNext() {
      var failed = false;
      state.sent = state.desired;
      state.promise = changeLine(lineKey, state.sent)
        .catch(function (error) {
          failed = true;
          showToast(error.code === 'HTTP_ERROR' ? error.message : str('cartUnknown', 'Warenkorb wurde abgeglichen. Bitte prüfe den aktuellen Stand.'), '');
          throw error;
        })
        .then(function (cart) {
          if (state.desired !== state.sent && state.sent !== 0) return sendNext();
          return cart;
        })
        .finally(function () {
          if (failed || state.desired === state.sent || state.sent === 0) {
            delete lineChanges[lineKey];
            state.promise = null;
            setLineBusy(lineKey, false);
          }
        });
      return state.promise;
    }

    return sendNext();
  }

  function addVariant(variantId, opts) {
    opts = opts || {};
    return performMutation(function () {
      return requestJson('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
    })
      .then(function (outcome) {
        if (opts.toastName) showToast(opts.toastName, str('toastSuffix', 'ist im Warenkorb'));
        if (!outcome.cart) {
          showToast(str('cartRefreshError', 'Artikel hinzugefügt. Der aktuelle Warenkorbstatus konnte nicht geladen werden.'), '');
        }
        return outcome.cart || outcome.response;
      })
      .catch(function (error) {
        showToast(error.message || str('addError', 'Konnte nicht hinzugefügt werden.'), '');
        throw error;
      })
      ;
  }

  /* ---------- toast ---------- */

  function showToast(name, suffix) {
    var toast = document.querySelector('[data-pg-toast]');
    if (!toast) return;
    var nameEl = toast.querySelector('[data-pg-toast-name]');
    var suffixEl = toast.querySelector('[data-pg-toast-suffix]');
    if (nameEl) nameEl.textContent = name;
    if (suffixEl) suffixEl.textContent = suffix || '';
    toast.hidden = false;
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.hidden = true; }, 3500);
  }

  function setFormStatus(form, message, isError) {
    if (!form) return;
    var status = form.querySelector('[data-pg-add-status]');
    if (!status) return;
    status.textContent = message || '';
    status.setAttribute('data-state', isError ? 'error' : 'info');
  }

  function addFailureMessage(error) {
    if (error && error.code === 'HTTP_ERROR') {
      return error.message || str('addError', 'Der Artikel wurde nicht hinzugefügt.');
    }
    return str('addUnknown', 'Die Verbindung wurde unterbrochen. Prüfe den Warenkorb, bevor du es erneut versuchst.');
  }

  /* ---------- add-modal ---------- */

  function modalEl() {
    return document.querySelector('[data-pg-addmodal]');
  }

  function openModal(item, form) {
    var displayTitle = item.product_title || item.title || '';
    if (window.PGProductTitle) displayTitle = window.PGProductTitle(item.handle, displayTitle);
    var modal = modalEl();
    if (!modal) {
      showToast(displayTitle || str('itemFallback', 'Artikel'), str('toastSuffix', 'ist im Warenkorb'));
      return;
    }

    var photo = modal.querySelector('[data-pg-addmodal-photo]');
    if (photo) {
      photo.textContent = '';
      if (item.image) {
        var img = document.createElement('img');
        img.src = item.image;
        img.alt = displayTitle;
        photo.appendChild(img);
      } else {
        photo.textContent = str('image', 'Bild');
      }
    }

    var nameEl = modal.querySelector('[data-pg-addmodal-name]');
    if (nameEl) nameEl.textContent = displayTitle;

    /* A1: gewählte Variante (z.B. «Grösse: L») unter dem Produktnamen —
       nur bei Nicht-Default-Varianten. pg-pdp.js pflegt data-pg-variant-title
       am Formular; Fallback: variant_title aus der Cart-API. */
    var sub = (form && form.dataset.pgProductSub) || '';
    var variantLabel = (form && form.dataset.pgVariantTitle) || '';
    if (!variantLabel && item.variant_title && item.variant_title !== 'Default Title') {
      variantLabel = item.variant_title;
    }
    if (variantLabel) sub = sub ? sub + ' · ' + variantLabel : variantLabel;
    var subEl = modal.querySelector('[data-pg-addmodal-sub]');
    if (subEl) subEl.textContent = sub;

    var priceEl = modal.querySelector('[data-pg-addmodal-price]');
    if (priceEl) priceEl.textContent = formatMoney(typeof item.final_price === 'number' ? item.final_price : item.price || 0);

    var warranty = modal.querySelector('[data-pg-warranty]');
    if (warranty) warranty.hidden = !(form && form.dataset.pgIsPc === 'true');

    // Hardware-Garantie + Lieferung nur bei PC-Produkten
    var isPc = form && form.dataset.pgIsPc === 'true';
    var hwWarranty = modal.querySelector('[data-pg-hw-warranty]');
    var delivery = modal.querySelector('[data-pg-delivery]');
    if (hwWarranty) hwWarranty.hidden = !isPc;
    if (delivery) delivery.hidden = !isPc;

    modal.hidden = false;
    var closeBtn = modal.querySelector('[data-pg-addmodal-close]');
    modalDialogToken = window.PGDialogs.open({
      element: modal,
      trigger: form && form.querySelector('[type="submit"]'),
      initialFocus: closeBtn,
      onRequestClose: function (restoreFocus) { closeModal(restoreFocus); }
    });
  }

  function closeModal(restoreFocus) {
    var modal = modalEl();
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    window.PGDialogs.close(modalDialogToken, restoreFocus);
    modalDialogToken = null;
  }

  function syncModalState(cart) {
    var modal = modalEl();
    if (!modal || !cart || !cart.items) return;

    var productIds = cart.items.map(function (i) { return String(i.product_id); });

    // Cross-sell rows
    modal.querySelectorAll('.pg-addmodal__upsell-row').forEach(function (row) {
      var inCart = productIds.indexOf(row.getAttribute('data-product-id')) !== -1;
      row.setAttribute('data-in-cart', inCart ? 'true' : 'false');
      var btn = row.querySelector('[data-pg-add]');
      if (btn) {
        btn.textContent = inCart ? str('inCart', '✓ Im Korb') : str('add', '+ Dazu');
        btn.disabled = inCart;
      }
    });

    // Footer total
    var totalEl = modal.querySelector('[data-pg-addmodal-total]');
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price || 0);
  }

  /* ---------- event delegation ---------- */

  document.addEventListener('click', function (e) {
    var qtyBtn = e.target.closest('[data-pg-qty-change]');
    if (qtyBtn) {
      var delta = parseInt(qtyBtn.getAttribute('data-pg-qty-change'), 10) || 0;
      var lineKey = qtyBtn.getAttribute('data-line-key');
      var current = lineChanges[lineKey] ? lineChanges[lineKey].desired : (parseInt(qtyBtn.getAttribute('data-qty'), 10) || 0);
      var qty = current + delta;
      if (qty < 0) qty = 0;
      queueLineChange(lineKey, qty).catch(function () {});
      return;
    }

    var removeBtn = e.target.closest('[data-pg-remove]');
    if (removeBtn) {
      queueLineChange(removeBtn.getAttribute('data-line-key'), 0).catch(function () {});
      return;
    }

    var addBtn = e.target.closest('[data-pg-add]');
    if (addBtn && !addBtn.disabled) {
      addBtn.disabled = true;
      addVariant(addBtn.getAttribute('data-variant-id'), {
        toastName: addBtn.getAttribute('data-product-title') || ''
      }).catch(function () {}).finally(function () {
        var modalRow = addBtn.closest('.pg-addmodal__upsell-row');
        addBtn.disabled = !!modalRow && modalRow.getAttribute('data-in-cart') === 'true';
      });
      return;
    }

    if (e.target.closest('[data-pg-addmodal-close]')) {
      closeModal();
      return;
    }

    var modal = modalEl();
    if (modal && !modal.hidden && e.target === modal) closeModal();
  });

  // PDP add-to-cart: intercept and show the modal instead of navigating
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-pg-ajax-add]');
    if (!form) return;
    e.preventDefault();
    if (form.getAttribute('data-pg-submitting') === 'true') return;
    form.setAttribute('data-pg-submitting', 'true');
    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    setFormStatus(form, str('adding', 'Wird zum Warenkorb hinzugefügt …'), false);
    performMutation(function () {
      return requestJson('cart/add.js', { method: 'POST', body: new FormData(form) });
    })
      .then(function (outcome) {
        openModal(outcome.response, form);
        setFormStatus(form, '', false);
        if (!outcome.cart) {
          var message = str('cartRefreshError', 'Artikel hinzugefügt. Der aktuelle Warenkorbstatus konnte nicht geladen werden.');
          setFormStatus(form, message, true);
          showToast(message, '');
        }
        return outcome.response;
      })
      .catch(function (error) {
        var message = addFailureMessage(error);
        setFormStatus(form, message, true);
        showToast(message, '');
      })
      .finally(function () {
        form.removeAttribute('data-pg-submitting');
        if (submitBtn) submitBtn.disabled = false;
      });
  });

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    cartRestoresPending += 1;
    setCartRefreshRequired(cartNeedsRefresh);
    enqueueMutation(reconcileCart).catch(function () {}).finally(function () {
      cartRestoresPending -= 1;
      setCartRefreshRequired(cartNeedsRefresh);
    });
  });

  // Initial header sync; restored pages reconcile through pageshow above.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { getCart().catch(function () {}); });
  } else {
    getCart().catch(function () {});
  }
})();
